export const TOOL_NAMES = {
  RECTANGLE: "RectangleROI",
  ELLIPSE: "EllipticalROI",
  CIRCLE: "CircleROI",
  ARROW: "ArrowAnnotate",
  FREEHAND: "PlanarFreehandROI",
};

export const SHAPE_TO_TOOL_NAME = {
  rectangle: TOOL_NAMES.RECTANGLE,
  ellipse: TOOL_NAMES.ELLIPSE,
  circle: TOOL_NAMES.CIRCLE,
  arrow: TOOL_NAMES.ARROW,
  freehand: TOOL_NAMES.FREEHAND,
};

export const TOOL_NAME_TO_SHAPE = Object.fromEntries(
  Object.entries(SHAPE_TO_TOOL_NAME).map(([k, v]) => [v, k])
);

export const SHAPE_LABEL = {
  rectangle: "Rectangle",
  ellipse: "Ellipse",
  circle: "Circle",
  arrow: "Arrow",
  freehand: "Freehand",
};

// Visual distinction: professor annotations green, student annotations orange.
// Kept for back-compat; per-user colors (below) are the default now.
export const colorForRole = (role) =>
  role === "Etudiant" ? "rgb(255, 165, 0)" : "rgb(0, 255, 0)";

export const rgbArrayForRole = (role) =>
  role === "Etudiant" ? [255, 165, 0] : [0, 255, 0];

// Per-user default color: each author gets one distinct, stable color picked
// deterministically from their identity. An explicit `color` on the annotation
// (chosen via the picker) always wins over this default.
const USER_COLOR_PALETTE = [
  [0, 200, 83], // green
  [255, 145, 0], // orange
  [41, 121, 255], // blue
  [213, 0, 249], // magenta
  [0, 184, 212], // cyan
  [255, 61, 0], // red
  [124, 77, 255], // violet
  [255, 196, 0], // amber
];

const hashIdentity = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export const rgbArrayForUser = (identity) =>
  USER_COLOR_PALETTE[hashIdentity(identity || "") % USER_COLOR_PALETTE.length];

export const colorForUser = (identity) =>
  `rgb(${rgbArrayForUser(identity).join(", ")})`;

// Parse "rgb(r, g, b)" / "rgba(...)" / "#rrggbb" → [r, g, b]; null if unparseable.
export const parseColor = (c) => {
  if (!c || typeof c !== "string") return null;
  const m = c.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  const hex = c.match(/^#?([0-9a-f]{6})$/i);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  return null;
};

// Resolve an annotation's display color: explicit pick wins, else per-user.
export const colorForAnno = (anno) =>
  anno?.color || colorForUser(anno?.authorName);

export const rgbArrayForAnno = (anno) =>
  parseColor(anno?.color) || rgbArrayForUser(anno?.authorName);

export const getShapeKey = (anno) => {
  if (anno?.toolName && TOOL_NAME_TO_SHAPE[anno.toolName]) return TOOL_NAME_TO_SHAPE[anno.toolName];
  if (anno?.bounds) return "rectangle";
  return null;
};

export const getGeometry = (anno) => {
  if (anno?.geometry) return anno.geometry;
  if (anno?.bounds) {
    return { x: anno.bounds.x, y: anno.bounds.y, width: anno.bounds.width, height: anno.bounds.height };
  }
  return null;
};

export const computeBboxGeometry = (p1, p2) => ({
  x: Math.min(p1.x, p2.x),
  y: Math.min(p1.y, p2.y),
  width: Math.abs(p1.x - p2.x),
  height: Math.abs(p1.y - p2.y),
});

export const isMeaningfulGeometry = (shape, geom, minSize = 0.003) => {
  if (!geom) return false;
  if (shape === "rectangle" || shape === "ellipse" || shape === "circle") {
    return geom.width > minSize && geom.height > minSize;
  }
  if (shape === "arrow") {
    return Math.hypot(geom.x2 - geom.x1, geom.y2 - geom.y1) > minSize;
  }
  if (shape === "freehand") {
    return Array.isArray(geom.points) && geom.points.length > 2;
  }
  return false;
};
